import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { moneyCompact } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { BarChart3, TrendingUp } from "lucide-react";

// Published benchmark bands for context (ConstructConnect).
const BANDS = "Hard-bid 10–20% · Private competitive 15–25% · Negotiated 30–50% · Relationship 50%+";

function SliceTable({ title, testid, rows, minSample }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-black mb-3">{title}</h3>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-4">No decided bids yet.</div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => {
            const thin = r.decided < minSample;
            return (
              <div key={r.key} data-testid={`slice-${r.key}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-slate-700 truncate pr-2">{r.key}</span>
                  <span className={`font-mono tabular-nums ${thin ? "text-slate-400" : "text-black"}`}>
                    {thin ? "—" : `${r.rate_count}%`} <span className="text-slate-400">· {r.won}/{r.decided}</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full ${thin ? "bg-slate-300" : "bg-[#003E87]"}`} style={{ width: `${thin ? 0 : r.rate_count}%` }} />
                </div>
                {thin && <div className="text-[10px] text-slate-400 mt-0.5">Too few to call ({r.decided} decided, need {minSample})</div>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default function HitRate() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/insights/hit-rate").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div className="px-6 sm:px-8 py-8">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#003E87]" /> Hit rate
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Win rate sliced by client, scope and size — never one blended number. Thin slices are withheld, not guessed.</p>
      </div>

      {!data ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : (
        <>
          <Card className="p-6 mb-6 flex flex-wrap items-center gap-8">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-4xl font-bold text-black tabular-nums">{data.overall.rate_count}%</span>
                <TrendingUp className="w-5 h-5 text-[#F4C400]" />
              </div>
              <div className="text-xs text-slate-500 mt-1">By count — {data.overall.won} won of {data.overall.decided} decided</div>
            </div>
            <div>
              <div className="font-display text-4xl font-bold text-black tabular-nums">{data.overall.rate_value}%</div>
              <div className="text-xs text-slate-500 mt-1">By dollar — {moneyCompact(data.overall.won_value)} of {moneyCompact(data.overall.decided_value)}</div>
            </div>
            <div className="flex-1 min-w-[240px] text-xs text-slate-400 border-l border-slate-100 pl-6">
              <div className="font-medium text-slate-500 mb-1">Benchmark bands</div>
              {BANDS}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SliceTable title="By client" testid="hitrate-by-client" rows={data.by_client} minSample={data.min_sample} />
            <SliceTable title="By primary discipline" testid="hitrate-by-primary-discipline" rows={data.by_primary_discipline} minSample={data.min_sample} />
            <SliceTable title="By scope completeness" testid="hitrate-by-completeness" rows={data.by_completeness} minSample={data.min_sample} />
            <SliceTable title="By discipline (scope type)" testid="hitrate-by-discipline" rows={data.by_discipline} minSample={data.min_sample} />
            <SliceTable title="By size band" testid="hitrate-by-size" rows={data.by_size} minSample={data.min_sample} />
          </div>
        </>
      )}
    </div>
  );
}
